/**
 * Scroll progress indicator for the mobile full-width product gallery.
 *
 * Purely presentational: it observes the scroll position of the existing
 * [data-product-media-list] slider and sizes/moves a bar accordingly.
 * It never changes the scroller itself, so the gallery keeps working
 * exactly as before if this script fails to load.
 */
(function () {
  'use strict';

  if (customElements.get('gallery-scroll-progress')) return;

  class GalleryScrollProgress extends HTMLElement {
    constructor() {
      super();

      this.bar = this.querySelector('[data-gallery-scroll-progress-bar]');
      this.slider = null;
      this.ticking = false;
      this.onScroll = this.onScroll.bind(this);
      this.update = this.update.bind(this);
    }

    connectedCallback() {
      const gallery = this.closest('product-images') || this.parentElement;
      this.slider = gallery ? gallery.querySelector('[data-product-media-list]') : null;

      if (!this.slider || !this.bar) {
        this.hidden = true;
        return;
      }

      this.slider.addEventListener('scroll', this.onScroll, {passive: true});
      window.addEventListener('resize', this.update);
      document.addEventListener('theme:resize:width', this.update);

      // Fires once after first layout too, which is when the slider can
      // finally be measured
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.update);
        this.resizeObserver.observe(this.slider);
      }

      window.addEventListener('load', this.update);
      requestAnimationFrame(this.update);
      this.update();
    }

    disconnectedCallback() {
      if (this.slider) {
        this.slider.removeEventListener('scroll', this.onScroll);
      }

      window.removeEventListener('resize', this.update);
      window.removeEventListener('load', this.update);
      document.removeEventListener('theme:resize:width', this.update);
      this.resizeObserver?.disconnect();
    }

    onScroll() {
      if (this.ticking) return;

      this.ticking = true;

      requestAnimationFrame(() => {
        this.update();
        this.ticking = false;
      });
    }

    update() {
      if (!this.slider || !this.bar) return;

      const visible = this.slider.clientWidth;

      // Not laid out yet (element upgraded before first layout) - keep the
      // current state and wait for the resize observer / load event
      if (!visible) return;

      const total = this.slider.scrollWidth;
      const scrollable = total - visible;

      // Nothing to scroll (single slide, or desktop layout where the bar is hidden)
      if (scrollable <= 1) {
        this.setAttribute('data-inactive', '');
        this.bar.style.width = '100%';
        this.bar.style.transform = 'translateX(0)';
        return;
      }

      this.removeAttribute('data-inactive');

      const ratio = Math.min(visible / total, 1);
      const progress = Math.min(Math.max(this.slider.scrollLeft / scrollable, 0), 1);
      // Travel is measured across this element's own track, not the slider
      const travel = this.clientWidth * (1 - ratio);

      this.bar.style.width = `${ratio * 100}%`;
      this.bar.style.transform = `translateX(${progress * travel}px)`;
    }
  }

  customElements.define('gallery-scroll-progress', GalleryScrollProgress);
})();
